import { matchPath } from 'react-router-dom';
import type { TourDefinition } from './types';

/**
 * The guided-tour catalogue (item 7.6).
 *
 * Every step targets a stable `data-testid` that the app shell always renders on
 * an authenticated screen (the primary navigation rail and the top-bar
 * affordances), so a tour can be launched from anywhere without first navigating
 * to a particular page. The selectors are kept in one place here and mirrored by
 * the `data-testid`s added to {@link AppShell}/{@link NavList} — change them
 * together.
 *
 * WHY target the nav rather than page innards: the rail is present on every
 * authenticated route and never unmounts mid-tour, which keeps highlighting
 * robust. Feature tours therefore teach the *journey* (which areas to visit, in
 * order) rather than pixel-hunting inside a single screen.
 *
 * Page tours complement the journeys: each one (in `./pages/`) is bound to a
 * route and highlights the controls *inside* that screen via `data-tour`
 * anchors. The shell's compass button launches the tour for the current page.
 */

/** Selector for a primary-navigation entry (mirrors {@link AppShell}). */
export function navSelector(key: string): string {
  return `[data-testid="nav-${key}"]`;
}

/** Selector for a top-bar affordance (mirrors {@link AppShell}). */
export function topbarSelector(key: string): string {
  return `[data-testid="topbar-${key}"]`;
}

/** The shell-level journey tours. Ordered as they appear in the Help Centre launcher. */
export const JOURNEY_TOURS: ReadonlyArray<TourDefinition> = [
  {
    id: 'product-overview',
    title: 'Product overview',
    description: 'A two-minute orientation: the dashboard, the estate and the deploy flow.',
    autoStart: true,
    steps: [
      {
        title: 'Welcome to DeployHub',
        description:
          'DeployHub manages the whole lifecycle of your applications — from release through deploy, monitoring and recovery. This quick tour shows where everything lives.',
      },
      {
        element: navSelector('dashboard'),
        title: 'Start at the Dashboard',
        description:
          'The Dashboard answers one question first: what needs your attention? Action-required and critical items surface at the top and deep-link to the screen that resolves them.',
        side: 'right',
        align: 'start',
      },
      {
        element: navSelector('customers'),
        title: 'Customers',
        description: 'Every customer you manage, with their lifecycle state and contacts.',
        side: 'right',
        align: 'start',
      },
      {
        element: navSelector('environments'),
        title: 'Environments',
        description:
          'Each customer application runs in one or more environments. Health and configuration drift are shown here at a glance.',
        side: 'right',
        align: 'start',
      },
      {
        element: navSelector('releases'),
        title: 'Releases',
        description: 'Versioned builds of an application, ready to be promoted and deployed.',
        side: 'right',
        align: 'start',
      },
      {
        element: navSelector('deployments'),
        title: 'Deployments',
        description:
          'Watch a release roll out to an environment as a live timeline of steps and logs, with retry and rollback when a step fails.',
        side: 'right',
        align: 'start',
      },
      {
        element: navSelector('agents'),
        title: 'Agents',
        description:
          'The lightweight agents installed on each host carry out the work and report their heartbeat back here.',
        side: 'right',
        align: 'start',
      },
      {
        element: topbarSelector('search'),
        title: 'Jump anywhere',
        description: 'Global search finds any customer, application, environment or release in seconds.',
        side: 'bottom',
        align: 'end',
      },
      {
        element: topbarSelector('notifications'),
        title: 'Stay informed',
        description: 'Approvals, failures and security alerts arrive here as notifications.',
        side: 'bottom',
        align: 'end',
      },
      {
        element: navSelector('help'),
        title: 'Help is always here',
        description:
          'Open the Help Centre any time to search the full guide or replay a tour. That is the end of the overview — enjoy!',
        side: 'right',
        align: 'start',
      },
    ],
  },
  {
    id: 'deploy-a-release',
    title: 'Deploy a release',
    description: 'Follow a release from the catalogue through promotion to a live deployment.',
    steps: [
      {
        element: navSelector('releases'),
        title: 'Pick a release',
        description:
          'Open Releases and choose the version you want to ship. A release is immutable — the same artefact travels across every environment.',
        side: 'right',
        align: 'start',
      },
      {
        element: navSelector('environments'),
        title: 'Choose the target',
        description:
          'Promoting a release to an environment runs the compatibility, guardrail and approval gates before anything is deployed.',
        side: 'right',
        align: 'start',
      },
      {
        element: navSelector('deployments'),
        title: 'Watch it roll out',
        description:
          'Once cleared, the deployment executes as a timeline of steps with live logs. If a step fails, the failure surface explains the cause and offers retry or rollback.',
        side: 'right',
        align: 'start',
      },
    ],
  },
  {
    id: 'set-up-an-approval',
    title: 'Set up an approval',
    description: 'Configure who signs off production changes and where approvals appear.',
    steps: [
      {
        element: navSelector('administration'),
        title: 'Assign approvers',
        description:
          'In Administration, Approver assignments decide who may sign off changes for each scope — including the senior override for emergencies.',
        side: 'right',
        align: 'start',
      },
      {
        element: navSelector('deployments'),
        title: 'Where approvals happen',
        description:
          'A production deployment pauses at "awaiting approval". An assigned approver reviews the impact assessment and approves or rejects it here.',
        side: 'right',
        align: 'start',
      },
      {
        element: topbarSelector('notifications'),
        title: 'Approvers are notified',
        description: 'When a change needs sign-off, the assigned approvers receive a notification here.',
        side: 'bottom',
        align: 'end',
      },
    ],
  },
  {
    id: 'onboard-an-environment',
    title: 'Onboard an environment',
    description: 'Bring a new customer environment under management, end to end.',
    steps: [
      {
        element: navSelector('customers'),
        title: 'Start with the customer',
        description: 'Create or open the customer that will own the new environment.',
        side: 'right',
        align: 'start',
      },
      {
        element: navSelector('environments'),
        title: 'Add the environment',
        description:
          'Define the environment and its target infrastructure. Provisioning brings the host, database, agent and application into existence in one guided run.',
        side: 'right',
        align: 'start',
      },
      {
        element: navSelector('agents'),
        title: 'Confirm the agent',
        description:
          'When the agent enrols and sends its first heartbeat it turns healthy here — the environment is now ready to receive deployments.',
        side: 'right',
        align: 'start',
      },
    ],
  },
];

/**
 * Load the per-screen page tours.
 *
 * WHY lazy: the page-tour copy (~95 kB of text across every module) would
 * otherwise sit in the entry chunk and blow the initial-payload budget
 * (docs/engineering/frontend-performance.md). Journeys stay static because the
 * auto-start overview must be available on first paint; page tours are only
 * needed once the shell is up, so they arrive in their own chunk.
 */
export async function loadPageTours(): Promise<ReadonlyArray<TourDefinition>> {
  const module = await import('./pages');
  return module.PAGE_TOURS;
}

/**
 * The page tour for a pathname, if one exists.
 *
 * Static patterns win over parameterised ones so `/promotions/new` resolves to
 * the request-form tour rather than the `/promotions/:id` detail tour (both
 * match under react-router's rules). Trailing-slash and case are handled by
 * `matchPath`.
 */
export function pageTourFor(
  pathname: string,
  catalogue: ReadonlyArray<TourDefinition>,
): TourDefinition | undefined {
  const candidates = catalogue.filter(
    (tour): tour is TourDefinition & { route: string } =>
      tour.route !== undefined && matchPath({ path: tour.route, end: true }, pathname) !== null,
  );
  return candidates.find((tour) => !tour.route.includes(':')) ?? candidates[0];
}

/** Find a tour by id within a catalogue (defaults to the always-loaded journeys). */
export function findTour(
  id: string,
  catalogue: ReadonlyArray<TourDefinition> = JOURNEY_TOURS,
): TourDefinition | undefined {
  return catalogue.find((tour) => tour.id === id);
}

/** The single auto-start tour, if any (the one shown once to new users). */
export function autoStartTour(): TourDefinition | undefined {
  return JOURNEY_TOURS.find((tour) => tour.autoStart === true);
}
